import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import readXlsxFile from "read-excel-file";
import { connect } from "react-redux";
import {
  addActionPlanTemp,
  downloadActionPlanTemplate,
  getMeasurementUnits,
} from "../../../store/rbm/actions";
import ActionPlanTableView from "./ActionPlanTableView";
import DownloadIcon from "@mui/icons-material/Download";
import ConfirmationDialog from "../../common/components/ConfirmationDialog";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import TableSkeleton from "../../common/components/TableSkeleton";
import AddToDriveRoundedIcon from "@mui/icons-material/AddToDriveRounded";
import {
  FC_AreDataValid,
  isWeightValid,
  MEASUREMENT_NAME_TO_CODE,
  RBM_REPORT_METHOD,
} from "./validations";

const excelHeaders = [
  "OutputNo",
  "OutputName",
  "OutputWeight",
  "Budget",
  "IndicatorNo",
  "IndicatorName",
  "FormulaType",
  "MeasurementUnit",
  "IndicatorWeight",
  "Baseline",
  "AnnualTarget",
  "Q1Target",
  "Q2Target",
  "Q3Target",
  "Q4Target",
  "ActivityNo",
  "ActivityName",
  "ActivityWeight",
  "ActivityQuarterNo",
  "TargetShare",
];

const AddActionPlan = ({
  onClose,
  open,
  loading,
  addActionPlanTemp,
  downloadActionPlanTemplate,
  selectedFiscalYear,
  setTotalRecods,
  measurementUnits,
  getMeasurementUnits,
}) => {
  const [validationError, setValidationError] = useState([]);
  const [validatedData, setValidatedData] = useState([]);
  const [validatedDataAPI, setValidatedDataAPI] = useState([]);
  const [selectedReportingMethod, setSelectedReportingMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [requestToSubmit, setRequestToSubmit] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!measurementUnits.length) {
      getMeasurementUnits();
    }
  }, []);

  const handleCloseDialog = () => {
    setValidationError([]);
    setValidatedData([]);
    setValidatedDataAPI([]);
    setIsSubmitting(false);
    setRequestToSubmit(false);
    onClose();
    // handleClearClick()
  };

  const handleClearClick = () => {
    // Clear the value of the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setValidationError([]);
    setValidatedData([]);
    setValidatedDataAPI([]);
  };

  const isNotNumber = (value) => {
    // Check if the value is a number.
    if (typeof value !== "number") {
      return true;
    }

    // Check if the value is NaN.
    if (isNaN(value)) {
      return true;
    }

    // Otherwise, the value is a valid number.
    return false;
  };

  const getSplitValue = (value) => {
    if (typeof value === "string") return value.split(";").map((v) => v.trim());
    if (typeof value === "number")
      return value
        .toString()
        .split(";")
        .map((v) => v.trim());
    return [];
  };

  const FC_HandleUploadedFile = (event) => {
    if (!event.target.files.length) {
      setValidationError([]);
      return;
    }
    let file = event.target.files[0];
    if (
      file.name.substr(-4).toLowerCase() === ".xls" ||
      file.name.substr(-5).toLowerCase() === ".xlsx"
    ) {
      FC_GenerateJSONData(file);
    } else {
      setValidationError([
        {
          type: "invalid-file",
          message: "Only Excel files are allowed!",
        },
      ]);
    }
  };

  const FC_GenerateJSONData = async (file) => {
    const hasError = [];
    const tempData = [];
    const tempDataAPI = [];

    try {
      if (!file) return;

      setLoadingExcel(true);
      const rows = await readXlsxFile(file);

      if (!rows || rows.length < 2) {
        setValidationError([
          {
            type: "no-data",
            message: "The uploaded file is empty or the template is broken.",
          },
        ]);
        setLoadingExcel(false);
        return;
      }

      // Validate headers
      const labelRow = rows[0];
      const headerRow = rows[1];

      const validLabels =
        labelRow[0]?.toLowerCase() === "outputs" &&
        labelRow[4]?.toLowerCase() === "indicators" &&
        labelRow[15]?.toLowerCase() === "activities";

      if (!validLabels) {
        hasError.push({
          type: "header-error",
          message:
            "Header section labels (Outputs, Indicators, Activities) are incorrect or missing.",
        });
      }

      if (JSON.stringify(headerRow) !== JSON.stringify(excelHeaders)) {
        hasError.push({
          type: "header-error",
          message:
            "Column headers have been modified. Please use the latest template.",
        });
      }

      if (hasError.length) {
        setValidationError(hasError);
        setLoadingExcel(false);
        return;
      }

      // Parse rows
      const rowData = rows.slice(2);
      let previous = { output: 0, indicator: 0, activity: 0 };
      let outputNo = 1,
        outputName = "",
        outputWeight = "",
        budget = 0,
        indicatorNo = 1,
        indicatorName = "",
        formulaType = "",
        formulaTypeCode = "",
        measurementUnit = "",
        indicatorWeight = "",
        baseline = 0,
        annualTarget = 0,
        q1Target = 0,
        q2Target = 0,
        q3Target = 0,
        q4Target = 0;

      for (let index = 0; index < rowData.length; index++) {
        const row = rowData[index];
        const rowIndex = index + 3;

        // --- Output columns
        outputNo = row[0] ? +row[0] : outputNo;
        outputName = row[1] || outputName;
        outputWeight = row[2] || outputWeight;
        budget = row[3] !== null ? +row[3] : budget;

        if (index === 0 && outputNo !== 1) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: First OutputNo must be 1.`,
          });
        } else if (
          index > 0 &&
          !isNotNumber(row[0]) &&
          outputNo - previous.output !== 1
        ) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: OutputNo must increase sequentially.`,
          });
        }

        if (previous.output !== outputNo) {
          previous = { output: outputNo, indicator: 0, activity: 0 };
        }

        if (!outputName) {
          hasError.push({
            type: "missing-field",
            message: `Row ${rowIndex}: OutputName is required.`,
          });
        }

        if (!isWeightValid(outputWeight)) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Outup Weight must be a High or Medium or Low.`,
          });
        }

        if (isNotNumber(budget)) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Budget must be a valid number.`,
          });
        }

        // --- Indicator columns
        indicatorNo = row[4] ? +row[4] : indicatorNo;
        indicatorName = row[5] || indicatorName;
        formulaType = row[6] || formulaType;
        measurementUnit = row[7] || measurementUnit;
        indicatorWeight = row[8] || indicatorWeight;
        baseline = row[9] !== null ? +row[9] : baseline;
        annualTarget = row[10] !== null ? +row[10] : annualTarget;
        q1Target = row[11] !== null ? +row[11] : q1Target;
        q2Target = row[12] !== null ? +row[12] : q2Target;
        q3Target = row[13] !== null ? +row[13] : q3Target;
        q4Target = row[14] !== null ? +row[14] : q4Target;

        if (previous.indicator !== indicatorNo) {
          previous.indicator = indicatorNo;
          previous.activity = 0;
        }

        if (!indicatorName) {
          hasError.push({
            type: "missing-field",
            message: `Row ${rowIndex}: IndicatorName is required.`,
          });
        }

        if (
          !formulaType ||
          !Object.keys(MEASUREMENT_NAME_TO_CODE).includes(formulaType)
        ) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Invalid FormulaType "${formulaType}".`,
          });
        } else {
          formulaTypeCode = MEASUREMENT_NAME_TO_CODE[formulaType];
        }

        if (!measurementUnit) {
          hasError.push({
            type: "missing-field",
            message: `Row ${rowIndex}: MeasurementUnit is required.`,
          });
        }

        if (!measurementUnit) {
          hasError.push({
            type: "missing-field",
            message: `Row ${rowIndex}: MeasurementUnit is required.`,
          });
        }

        if (!isWeightValid(indicatorWeight)) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Indicator Weight must be a High or Medium or Low.`,
          });
        }

        const numericFields = [
          baseline,
          annualTarget,
          q1Target,
          q2Target,
          q3Target,
          q4Target,
        ];
        if (numericFields.some((num) => isNotNumber(num))) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Baseline and targets must be numbers.`,
          });
        }

        // --- Activity columns
        const activityName = row[16];
        const activityWeight = row[17];
        const activityQuarterNoList = getSplitValue(row[18]);
        const targetShareList = getSplitValue(row[19]);

        previous.activity += 1;
        const activityNo = previous.activity;

        if (!activityName) {
          hasError.push({
            type: "missing-field",
            message: `Row ${rowIndex}: ActivityName is required.`,
          });
        }

        if (!isWeightValid(activityWeight)) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: Activity Weight must be a High or Medium or Low.`,
          });
        }

        if (
          activityQuarterNoList.length === 0 ||
          activityQuarterNoList.length > 4 ||
          activityQuarterNoList.some((q) => !["1", "2", "3", "4"].includes(q))
        ) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: ActivityQuarterNo must contain 1–4 values (1, 2, 3, or 4).`,
          });
        }

        if (
          targetShareList.length !== 1 &&
          targetShareList.length !== activityQuarterNoList.length
        ) {
          hasError.push({
            type: "invalid-value",
            message: `Row ${rowIndex}: TargetShare must have either 1 value or exactly ${activityQuarterNoList.length} values.`,
          });
        }

        // Split activity rows
        activityQuarterNoList.forEach((q, i) => {
          const share =
            targetShareList.length === activityQuarterNoList.length
              ? +targetShareList[i]
              : +targetShareList[0];

          const sharedData = {
            outputNo,
            outputName,
            outputWeight,
            budget,
            indicatorNo,
            indicatorName,
            formulaType,
            measurementUnit,
            indicatorWeight,
            baseline,
            annualTarget,
            q1Target,
            q2Target,
            q3Target,
            q4Target,
            activityNo,
            activityName,
            activityWeight,
            activityQuarterNo: +q,
            targetShare: share,
          };

          tempData.push(sharedData);

          const selectedMeasurement = measurementUnits.find(
            (item) => item.code === formulaTypeCode
          );

          tempDataAPI.push({
            ...sharedData,
            outputWeight:
              outputWeight === "High" ? 3 : outputWeight === "Medium" ? 2 : 1,
            indicatorWeight:
              indicatorWeight === "High"
                ? 3
                : indicatorWeight === "Medium"
                ? 2
                : 1,
            activityWeight:
              activityWeight === "High"
                ? 3
                : activityWeight === "Medium"
                ? 2
                : 1,
            measurementUnitId: selectedMeasurement?.id || null,
          });
        });
      }

      const logicErrors = FC_AreDataValid(tempData);

      if (tempData.length > 0 && !hasError.length && !logicErrors.length) {
        setValidatedData(tempData);
        setValidatedDataAPI(tempDataAPI);
      } else {
        setValidationError([...hasError, ...logicErrors]);
      }

      setLoadingExcel(false);
    } catch (error) {
      console.error(error);
      setValidationError([
        {
          type: "exception",
          message: "Unexpected error while reading the file.",
        },
      ]);
      setLoadingExcel(false);
    }
  };

  const handleSubmit = () => {
    if (
      validatedDataAPI.length > 0 &&
      !validationError.length &&
      !!selectedReportingMethod
    ) {
      addActionPlanTemp(
        { reportMethod: selectedReportingMethod, data: validatedDataAPI },
        setTotalRecods,
        (status, res) => {
          setIsSubmitting(status);
          if (res) handleCloseDialog();
        }
      );
    }
  };

  return (
    <>
      <Dialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        // maxWidth="xl"
        fullScreen
      >
        <DialogTitle className=" text-uppercase ">
          <span className="d-flex align-items-center">
            Upload Action Plan{" "}
            <span className="text-primary d-flex align-items-center">
              <span className="material-icons">chevron_right</span> FY:{" "}
              {selectedFiscalYear.name}
            </span>
          </span>

          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <span className="material-icons">close</span>
          </IconButton>
          <div
            className="text-center mb-1 text-uppercase"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            Upload Action Plan
          </div>
          <div className="d-flex justify-content-center">
            <div className="custom-file" style={{ maxWidth: "750px" }}>
              <input
                name="selectFile"
                type="file"
                className="custom-file-input"
                accept=".xlsx, .xls, .csv"
                multiple={false}
                onChange={FC_HandleUploadedFile}
                onClick={handleClearClick}
                ref={fileInputRef}
              />
              <label className="custom-file-label">Choose file...</label>
            </div>
          </div>
          <div className="text-center mt-3">
            {!validationError.length && validatedData.length ? (
              <Typography className="d-inline alert alert-success">
                <VerifiedRoundedIcon /> Data verified successful!
              </Typography>
            ) : (
              <button
                className={`btn btn-link btn-sm  ${
                  loading || isSubmitting ? "disabled" : ""
                }`}
                disabled={loading || isSubmitting}
                onClick={() => downloadActionPlanTemplate()}
              >
                <DownloadIcon /> Download action plan template
              </button>
            )}
          </div>
        </DialogTitle>
        {/* {isSubmitting && <LinearProgress />} */}
        <DialogContent dividers>
          {loadingExcel ? (
            <div className="table-responsive">
              <table
                className="table table-sm table-bordered fixTableHead"
                style={{ fontSize: "10px" }}
              >
                <thead>
                  <tr>
                    <th className="text-center bg-output">Output No</th>
                    <th className="text-center bg-output">Output Name</th>
                    <th className="text-center bg-output">Output Weight</th>
                    <th className="text-center bg-output">Budget</th>
                    <th className="text-center bg-indicator">Indicator Name</th>
                    <th className="text-center bg-indicator">
                      Measurement Unit
                    </th>
                    <th className="text-center bg-indicator">
                      Indicator Weight
                    </th>
                    <th className="text-center bg-indicator">Baseline</th>
                    <th className="text-center bg-indicator">Annual Target</th>
                    <th className="text-center bg-indicator">Q1 Target</th>
                    <th className="text-center bg-indicator">Q2 Target</th>
                    <th className="text-center bg-indicator">Q3 Target</th>
                    <th className="text-center bg-indicator">Q4 Target</th>
                    <th className="text-center bg-activity">Activity No</th>
                    <th className="text-center bg-activity">Activity Name</th>
                    <th className="text-center bg-activity">Activity Weight</th>
                    <th className="text-center bg-activity">
                      Activity Quarter No
                    </th>
                    <th className="text-center bg-activity">Target Share</th>
                  </tr>
                </thead>
                <TableSkeleton cols={18} rows={20} />
              </table>
            </div>
          ) : (
            validationError.length > 0 && (
              <div className="my-3 border border-danger px-3 py-2">
                {validationError.map((error, index) => (
                  <div
                    className="d-flex align-items-start my-1"
                    style={{ fontSize: "12px" }}
                    key={index}
                  >
                    <div className="badge badge-danger my-1 mr-1">
                      {error.type}
                    </div>
                    <div>{error.message}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {!loadingExcel && validatedData.length > 0 && (
            <ActionPlanTableView data={validatedData} />
          )}

          {!loadingExcel &&
            !validationError.length &&
            !validatedData.length && (
              <div className="d-flex align-items-center justify-content-center text-success h-100">
                <div className="d-flex flex-column align-items-center">
                  <AddToDriveRoundedIcon style={{ fontSize: "72px" }} />
                  <span className="mt-4" style={{ fontSize: "24px" }}>
                    Upload Action Plan Excel File
                  </span>
                </div>
              </div>
            )}
        </DialogContent>
        <DialogActions className="d-flex justify-content-center py-4">
          <div className="d-flex justify-content-center">
            <div className="d-flex align-items-center flex-column">
              <Typography
                variant="subtitle1"
                gutterBottom
                className="text-primary font-weight-bold"
              >
                Select Reporting Method
              </Typography>
              <div className="d-flex align-items-center mb-2">
                {RBM_REPORT_METHOD.map((type) => (
                  <Chip
                    key={type.value}
                    icon={
                      <>
                        {selectedReportingMethod === type.value ? (
                          <span className="material-icons-round">
                            check_circle
                          </span>
                        ) : (
                          <span className="material-icons-round">
                            radio_button_unchecked
                          </span>
                        )}
                      </>
                    }
                    label={type.label}
                    color="success"
                    variant={
                      selectedReportingMethod !== type.value
                        ? "outlined"
                        : "filled"
                    }
                    size="small"
                    className="mr-2"
                    disabled={
                      validationError.length ||
                      !validatedData.length ||
                      loading ||
                      isSubmitting
                    }
                    onClick={() => setSelectedReportingMethod(type.value)}
                  />
                ))}
              </div>

              <Alert
                variant="outlined"
                severity="warning"
                sx={{
                  paddingTop: 0.1,
                  paddingBottom: 0.1,
                  paddingLeft: 0.7,
                  paddingRight: 0.7,
                  marginBottom: 1,
                }}
              >
                The selected reporting method will define how employee can
                report frequently.
              </Alert>

              <Button
                color="success"
                variant="contained"
                type="button"
                disabled={
                  validationError.length ||
                  !validatedData.length ||
                  loading ||
                  isSubmitting ||
                  !selectedReportingMethod
                }
                onClick={() => setRequestToSubmit(true)}
                style={{
                  float: "right",
                  borderRadius: "8px",
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: ".75rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                size="md"
              >
                <span className="material-icons mr-1">task_alt</span> Confirm
                Action Plan
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>
      {requestToSubmit && (
        <ConfirmationDialog
          confirmationDialog={true}
          message={`Are you sure you want to confirm this Action Plan?`}
          setConfirmationDialog={() => setRequestToSubmit(false)}
          onYes={handleSubmit}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  loading,
  user,
  selectedFiscalYear,
  measurementUnits,
}) => {
  return {
    loading,
    user,
    selectedFiscalYear,
    measurementUnits,
  };
};

export default connect(mapStateToProps, {
  downloadActionPlanTemplate,
  addActionPlanTemp,
  getMeasurementUnits,
})(AddActionPlan);
